import { isSafeFieldName } from '../../../../utils/validation.utils';
import {
  type DataProviderSet,
  DataType,
  isBoxBreakdownDataSet,
  isBoxDataPoint,
} from '../../../DataProvider/types';

export type EntityIdentifier = {
  entity: string;
  id?: string;
};

export const createEntityIdentifier = (
  entity: string,
  id?: string | null
): EntityIdentifier => {
  const trimmedEntity = entity.trim();
  const trimmedId = id?.trim();

  return {
    entity: trimmedEntity,
    ...(trimmedId && { id: trimmedId }),
  };
};

export const entityKey = (e: EntityIdentifier): string => e.id ?? e.entity;

export interface NormalizedPoint {
  /** Entity identifier (dim, label, or field value) */
  entity: string;
  id?: string;
  /** Segment identifier for breakdowns (outer dim) */
  segment?: string;
  /** Date for timeseries data */
  date?: string;
  /** Numeric value, null for missing data */
  value: number | null;
  /** Additional attributes from raw data (for TABLE_ROWS, SENTIMENT, etc.) */
  attributes: Record<string, unknown>;
}

export type ExtractionOptions = {
  /**
   * For BOX/BREAKDOWN_BOX: which percentile to extract.
   * Defaults to q50 (median).
   */
  percentile?: 'q10' | 'q50' | 'q90';

  /**
   * For TABLE_ROWS: which field to extract from each row record.
   * For SENTIMENT_SINGLE: which numeric field to extract ("value" | "count" | "rawValue").
   */
  field?: string;

  /**
   * Optional dimension filters (string equality match).
   *
   * - `segment`: used for BREAKDOWN_* datasets where `dataset.data[].dim` is the segment key.
   * - `entity`: used for SINGLE/TIMESERIES (series dim) and nested breakdown items (`breakdown[].dim`).
   * - `label`: used for datasets keyed by `label` (SENTIMENT_SINGLE, MAP).
   */
  segment?: string;
  entity?: string;
  label?: string;

  /**
   * For TIMESERIES: extract a specific date (YYYY-MM-DD) if provided.
   * If provided and no point matches, returns an empty array.
   */
  date?: string;

  /**
   * For TABLE_ROWS: optional row filter before extracting `field`.
   * Example: whereField="current_company" equals="Workday"
   */
  whereField?: string;
  equals?: string;

  /**
   * For TABLE_ROWS ranking/arg helpers: which field contains the "entity label"
   * (e.g., "current_company").
   */
  labelField?: string;

  /**
   * For nested structures: whether to flatten all nested values (arrays) when extracting.
   * Defaults to true.
   */
  flattenNested?: boolean;

  /**
   * For TIMESERIES/BREAKDOWN_TIMESERIES: aggregate across all time points or extract
   * only the first/latest point from each series.
   * Defaults to "all".
   */
  timeAggregation?: 'all' | 'latest' | 'first';

  /**
   * For TIMESERIES/BREAKDOWN_TIMESERIES with timeAggregation: when set, exclude the
   * entity matching this identifier (id or name) from the extracted values.
   * Used for "average among peers" (AVG excluding the selected company).
   */
  excludeEntity?: string;

  /**
   * For BREAKDOWN_SINGLE: which attribute to extract — "value" (count) or "share" (0..1).
   * Defaults to "value".
   */
  property?: 'value' | 'share';
};

export const safeNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const matchesEntity = (
  item: EntityIdentifier,
  target: string
): boolean => {
  const trimmedTarget = target.trim();
  if (!trimmedTarget) return false;

  // Try ID match first (if both have IDs)
  if (item.id && item.id === trimmedTarget) return true;

  // Fall back to name match
  return item.entity === trimmedTarget;
};

const extractNumbersFromUnknown = (
  value: unknown,
  flattenNested: boolean,
  depth = 0
): (number | null)[] => {
  if (depth > 4) return [];

  const direct = safeNumber(value);
  if (direct !== null) return [direct];

  if (Array.isArray(value)) {
    if (!flattenNested) return [];
    return value.flatMap((v) =>
      extractNumbersFromUnknown(v, flattenNested, depth + 1)
    );
  }

  return [];
};

/**
 * Normalizes any DataProviderSet into a standard array of NormalizedPoint objects.
 * This eliminates the need for dataset-type-specific switch statements in downstream processing.
 * @param dataset The dataset to normalize
 * @returns Array of normalized points
 */
export const normalizeDataset = (
  dataset: DataProviderSet
): NormalizedPoint[] => {
  switch (dataset.type) {
    case DataType.SINGLE:
      return dataset.data.map((point) => {
        const entityId = createEntityIdentifier(point.dim, point.id ?? null);
        return {
          ...entityId,
          value: safeNumber(point.value),
          attributes: point,
        };
      });

    case DataType.TIMESERIES:
      return dataset.data.flatMap((series) => {
        const entityId = createEntityIdentifier(series.dim, series.id ?? null);
        return series.timeseries.map((point) => ({
          ...entityId,
          date: point.date,
          value: safeNumber(point.value),
          attributes: { ...point, seriesDim: series.dim },
        }));
      });

    case DataType.BREAKDOWN_SINGLE:
      return dataset.data.flatMap((group) => {
        const segment = group.dim.trim();
        return group.breakdown.map((point) => {
          const entityId = createEntityIdentifier(point.dim, point.id ?? null);
          return {
            ...entityId,
            segment: segment,
            value: safeNumber(point.value),
            attributes: { ...point, segmentDim: group.dim },
          };
        });
      });

    case DataType.BREAKDOWN_TIMESERIES:
      return dataset.data.flatMap((group) => {
        const dim = group.dim.trim();
        return group.breakdown.map((point) => ({
          entity: dim,
          segment: dim,
          date: point.date,
          value: safeNumber(point.value),
          attributes: { ...point, segmentDim: group.dim },
        }));
      });

    case DataType.BOX:
      if (isBoxBreakdownDataSet(dataset)) {
        return dataset.data.flatMap((group) => {
          const segment = group.dim.trim();
          return group.breakdown.map((point) => {
            const entityId = createEntityIdentifier(
              point.dim,
              point.id ?? null
            );
            return {
              ...entityId,
              segment: segment,
              value: safeNumber(point.box.q50),
              attributes: { ...point, segmentDim: group.dim },
            };
          });
        });
      }
      return dataset.data.map((point) => {
        const entityId = createEntityIdentifier(point.dim, point.id ?? null);
        return {
          ...entityId,
          value: safeNumber(point.box.q50),
          attributes: point,
        };
      });

    case DataType.DISTRIBUTION:
      return dataset.data.flatMap((dist) => {
        const entityId = createEntityIdentifier(dist.dim, dist.id ?? null);
        return dist.distribution.map((point) => ({
          ...entityId,
          value: safeNumber(point.value),
          attributes: { ...point, distributionDim: dist.dim },
        }));
      });

    case DataType.MAP:
      return dataset.points.map((point) => {
        const entityId = createEntityIdentifier(point.label, point.id ?? null);
        return {
          ...entityId,
          value: safeNumber(point.value),
          attributes: point,
        };
      });

    case DataType.TABLE_ROWS:
      return dataset.data.map((row, index) => ({
        entity: String(index), // Default entity is row index
        value: null, // Will be extracted via field option
        attributes: row,
      }));

    case DataType.TEXT:
      return dataset.data.map((point) => {
        const entityId = createEntityIdentifier(point.entity, point.id ?? null);
        return {
          ...entityId,
          value: null,
          attributes: { ...point, text: point.text },
        };
      });

    default:
      return [];
  }
};

/**
 * Extracts numeric values from a DataProviderSet for aggregation.
 * Returns null for missing/invalid values to distinguish from zero.
 * @param dataset The dataset to extract values from
 * @returns Array of numeric values or null, or empty array if dataset is not aggregatable
 */
export const extractDatasetValues = (
  dataset?: DataProviderSet,
  options: ExtractionOptions = {}
): (number | null)[] => {
  if (!dataset) return [];

  const normalizedPoints = normalizeDataset(dataset);

  // Apply filters based on options
  let filtered = normalizedPoints;

  const targetEntity = options.entity?.trim() || options.label?.trim();
  const segment = options.segment?.trim();

  const isBreakdownWithBoth =
    (dataset.type === DataType.BREAKDOWN_SINGLE ||
      (dataset.type === DataType.BOX && isBoxBreakdownDataSet(dataset))) &&
    segment !== undefined &&
    segment !== '' &&
    targetEntity !== undefined &&
    targetEntity !== '';

  if (isBreakdownWithBoth) {
    filtered = filtered.filter(
      (point) =>
        point.segment !== undefined &&
        ((point.segment === segment && matchesEntity(point, targetEntity)) ||
          (point.segment === targetEntity && matchesEntity(point, segment)))
    );
  } else {
    if (targetEntity) {
      filtered = filtered.filter((point) => matchesEntity(point, targetEntity));
    }
    if (segment) {
      filtered = filtered.filter(
        (point) => point.segment !== undefined && point.segment === segment
      );
    }
  }

  // Date filtering (for timeseries)
  const date = options.date?.trim();
  if (date) {
    filtered = filtered.filter(
      (point) => point.date !== undefined && point.date === date
    );
  }

  const excludeEntity = options.excludeEntity?.trim();
  if (excludeEntity) {
    filtered = filtered.filter((point) => !matchesEntity(point, excludeEntity));
  }

  // TABLE_ROWS specific filtering
  if (dataset.type === DataType.TABLE_ROWS) {
    const { field, whereField, equals, flattenNested } = options;

    // Validate field safety first
    if (field && !isSafeFieldName(field)) return [];

    // Row filtering by whereField
    if (whereField && equals && isSafeFieldName(whereField)) {
      filtered = filtered.filter(
        (point) => String(point.attributes[whereField]) === equals
      );
    }

    // Field-based value extraction
    if (field && isSafeFieldName(field)) {
      return filtered.flatMap((point) =>
        extractNumbersFromUnknown(
          point.attributes[field],
          flattenNested ?? true
        )
      );
    }
  }

  if (dataset.type === DataType.SINGLE) {
    const requested = options.field;
    const singleField =
      requested === 'count' || requested === 'rawValue' || requested === 'value'
        ? requested
        : 'value';

    return filtered.map((point) => safeNumber(point.attributes[singleField]));
  }

  if (dataset.type === DataType.BOX && options.percentile) {
    const percentile = options.percentile;
    return filtered.map((point) => {
      const box = point.attributes.box;
      return isBoxDataPoint(box) ? safeNumber(box[percentile]) : null;
    });
  }

  // BREAKDOWN_SINGLE property: share vs value
  if (
    dataset.type === DataType.BREAKDOWN_SINGLE &&
    options.property === 'share'
  ) {
    return filtered.map((point) => safeNumber(point.attributes.share));
  }

  // Timeseries aggregation
  if (
    (dataset.type === DataType.TIMESERIES ||
      dataset.type === DataType.BREAKDOWN_TIMESERIES) &&
    options.timeAggregation
  ) {
    const mode = options.timeAggregation;
    if (mode !== 'all') {
      // Group by entity for aggregation
      const byEntity = new Map<string, NormalizedPoint[]>();
      filtered.forEach((point) => {
        const entity = point.entity;
        if (!byEntity.has(entity)) byEntity.set(entity, []);
        const entityPoints = byEntity.get(entity);

        if (entityPoints) {
          entityPoints.push(point);
        }
      });

      const result: (number | null)[] = [];
      const excludeEntity = options.excludeEntity?.trim();
      byEntity.forEach((points) => {
        if (
          excludeEntity &&
          points.length > 0 &&
          points[0] &&
          matchesEntity(points[0], excludeEntity)
        ) {
          return;
        }
        // Sort by date for time aggregation
        points.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

        if (mode === 'first' && points.length > 0) {
          const firstPoint = points[0];
          if (firstPoint) {
            result.push(firstPoint.value);
          }
        } else if (mode === 'latest' && points.length > 0) {
          const lastPoint = points[points.length - 1];
          if (lastPoint) {
            result.push(lastPoint.value);
          }
        }
      });
      return result;
    }
  }

  // Default: return all filtered values
  return filtered.map((point) => point.value);
};
